import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import { toast } from "sonner";
import { GoogleLogo, EnvelopeSimple, Lock, User } from "@phosphor-icons/react";
import api from "../lib/api";

// Map intent → post-signup landing page. Keeps the user in the flow they
// came for instead of dumping them on an empty dashboard.
const INTENT_LANDING = {
  track: "/dashboard/add",
  wishlist: "/dashboard/wishlist",
  vault: "/dashboard/vault/new",
};

export default function Signup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { registerEmail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Capture intent + tour ref from the URL on mount. Persist intent so
  // the post-OAuth round-trip can deep-link to the right add page, and
  // capture tour ref so we can fire a tour→signup GA4 conversion at
  // submit time.
  const fromTour = params.get("ref") === "tour";
  useEffect(() => {
    const intent = params.get("intent");
    const retailer = params.get("retailer");
    if (intent) {
      try { window.localStorage.setItem("pg_intent", intent); } catch { /* no-op */ }
    }
    if (retailer) {
      try { window.localStorage.setItem("pg_intent_retailer", retailer); } catch { /* no-op */ }
    }
    if (params.get("ref") === "tour") {
      try { window.localStorage.setItem("pg_signup_ref", "tour"); } catch { /* no-op */ }
    }
  }, [params]);

  const resolveLanding = () => {
    let intent = null;
    try { intent = window.localStorage.getItem("pg_intent"); } catch { /* no-op */ }
    if (intent && INTENT_LANDING[intent]) {
      try { window.localStorage.removeItem("pg_intent"); } catch { /* no-op */ }
      return INTENT_LANDING[intent];
    }
    return "/dashboard";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await registerEmail(email, password, name);
      toast.success("Account created");
      // GA4 conversion event - if this signup originated from /tour we fire
      // tour_signup_complete so we can measure tour ROI separately from
      // organic signups. signup_complete fires from AuthContext for both.
      try {
        let ref = null;
        try { ref = window.localStorage.getItem("pg_signup_ref"); } catch { /* no-op */ }
        if (ref === "tour" && window.gtag) {
          window.gtag("event", "tour_signup_complete", { method: "email" });
        }
        try { window.localStorage.removeItem("pg_signup_ref"); } catch { /* no-op */ }
      } catch { /* no-op */ }
      navigate(resolveLanding());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      const { data } = await api.get("/auth/google/config");
      if (!data?.enabled || !data?.client_id) {
        toast.error("Google sign-in is temporarily unavailable. Please create an account with email and password.");
        return;
      }
      const redirectUri = window.location.origin + "/auth/google/callback";
      const params = new URLSearchParams({
        client_id: data.client_id,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid email profile",
        access_type: "online",
        prompt: "select_account",
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    } catch (e) {
      toast.error("Could not start Google sign-in. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header minimal />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 hidden lg:block border border-[#e4e4e7] bg-[#0a0a0a] text-white p-12">
          <div className="pg-overline mb-6 text-white/60">Start tracking</div>
          <h2 className="font-heading text-5xl font-black tracking-tighter leading-[0.95]">A two-minute<br/>setup. Lifetime<br/>of saved cash<span className="text-[#002FA7]">.</span></h2>
          <p className="text-white/70 mt-6 max-w-md">Free forever for up to 5 items. Track purchases, store receipts, and never miss a warranty expiry - all in one vault.</p>
        </div>
        <div className="lg:col-span-5">
          {fromTour && (
            <div className="border border-[#D97706] bg-[#FFFBEB] px-4 py-3 mb-6 inline-flex items-center gap-2 text-sm" data-testid="signup-from-tour-ribbon">
              <span className="bg-[#D97706] text-white text-[10px] font-bold tracking-widest uppercase px-2 py-0.5">From the tour</span>
              <span className="text-[#0a0a0a]">Welcome back. Pro is on the house during launch.</span>
            </div>
          )}
          <div className="pg-overline mb-4">Create account</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-8">Join PriceGuard NZ</h1>

          <button onClick={handleGoogle} className="w-full border border-black font-bold px-6 py-3 inline-flex items-center justify-center gap-3 hover:bg-black hover:text-white transition-colors" data-testid="signup-google-btn">
            <GoogleLogo size={20} weight="bold" /> Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-[#e4e4e7]" />
            <span className="pg-overline">or</span>
            <div className="flex-1 h-px bg-[#e4e4e7]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
            <label className="block">
              <span className="pg-overline">Full name</span>
              <div className="mt-2 flex items-center border border-[#e4e4e7] focus-within:border-[#002FA7]">
                <User size={18} className="mx-3 text-[#52525b]" />
                <input type="text" required value={name} onChange={(e)=>setName(e.target.value)} className="flex-1 py-3 pr-3 outline-none bg-white text-sm" placeholder="Sam from Auckland" data-testid="signup-name-input" />
              </div>
            </label>
            <label className="block">
              <span className="pg-overline">Email</span>
              <div className="mt-2 flex items-center border border-[#e4e4e7] focus-within:border-[#002FA7]">
                <EnvelopeSimple size={18} className="mx-3 text-[#52525b]" />
                <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="flex-1 py-3 pr-3 outline-none bg-white text-sm" placeholder="you@example.co.nz" data-testid="signup-email-input" />
              </div>
            </label>
            <label className="block">
              <span className="pg-overline">Password</span>
              <div className="mt-2 flex items-center border border-[#e4e4e7] focus-within:border-[#002FA7]">
                <Lock size={18} className="mx-3 text-[#52525b]" />
                <input type="password" required minLength={8} value={password} onChange={(e)=>setPassword(e.target.value)} className="flex-1 py-3 pr-3 outline-none bg-white text-sm" placeholder="At least 8 characters" data-testid="signup-password-input" />
              </div>
              <p className="text-[11px] text-[#52525b] mt-1.5 font-mono-pg" data-testid="signup-password-hint">
                8+ chars · mix letters with numbers or symbols · no breach-list passwords
              </p>
            </label>
            <button type="submit" disabled={busy} className="w-full bg-[#002FA7] text-white font-bold px-6 py-3 hover:bg-[#00207A] disabled:opacity-60" data-testid="signup-submit-btn">
              {busy ? "Creating account…" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#52525b]">Already have one? <Link to="/login" className="font-bold text-black underline" data-testid="signup-to-login">Sign in</Link>.</p>
        </div>
      </div>
    </div>
  );
}
